import React, { useState, useRef } from 'react';
import Modal from '../../../../../../components/common/Modal';
import Button from '../../../../../../components/common/Button/Button';
import { translate } from '../../../../../../utils/Common';
import * as FaIcons from 'react-icons/fa';
import { FiUpload, FiImage } from 'react-icons/fi';
import styles from './UpdateLogoModal.module.css';

const UpdateLogoModal = ({ isOpen, onClose, currentLogo, onSave, companyName }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setLogoError(translate('dashboard.companies.detail.logo.errors.invalidFileType'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setLogoError(translate('dashboard.companies.detail.logo.errors.fileTooLarge'));
      return;
    }

    setLogoError('');
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.onerror = () => {
      setLogoError(translate('dashboard.companies.detail.logo.errors.readError'));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!logoFile) {
      setLogoError(translate('dashboard.companies.detail.logo.errors.noFileSelected'));
      return;
    }

    setLogoError('');
    setIsSaving(true);
    
    try {
      const result = await onSave(logoFile);
      
      if (result.success) {
        // Reset state and close modal
        setLogoFile(null);
        setLogoPreview(null);
        setLogoError('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onClose();
      } else {
        setLogoError(result.error || translate('dashboard.companies.detail.logo.errors.updateError'));
      }
    } catch (error) {
      setLogoError(error.message || translate('dashboard.companies.detail.logo.errors.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setLogoFile(null);
      setLogoPreview(null);
      setLogoError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  // Only show current logo if it's not the default placeholder
  const displayLogo = logoPreview || (currentLogo && !currentLogo.includes('/img/empty_company.png') ? currentLogo : null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('dashboard.companies.detail.logo.modal.title')}
      size="md"
      closeOnBackdropClick={!isSaving}
    >
      <div className={styles.container}>
        {/* Current Logo Display */}
        <div className={styles.logoSection}>
          <div className={styles.logoPreviewContainer}>
            {displayLogo ? (
              <div className={styles.logoWrapper}>
                <img 
                  src={displayLogo}
                  alt={companyName || translate('dashboard.companies.detail.logo.alt')}
                  className={styles.logoPreview}
                />
                {logoPreview && (
                  <div className={styles.previewBadge}>
                    <span>{translate('dashboard.companies.detail.logo.previewBadge')}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.logoPlaceholder}>
                <FiImage size={48} />
                <span>{translate('dashboard.companies.detail.logo.noLogo')}</span>
              </div>
            )}
          </div>
        </div>

        {/* File Upload Section */}
        <div className={styles.uploadSection}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className={styles.fileInput}
            id="logo-upload"
            disabled={isSaving}
          />
              <label 
                htmlFor="logo-upload" 
                className={styles.uploadLabel}
              >
                <FiUpload size={20} />
                <span>{logoFile ? translate('dashboard.companies.detail.logo.actions.changeImage') : translate('dashboard.companies.detail.logo.actions.selectImage')}</span>
              </label>
          
          {logoFile && (
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{logoFile.name}</span>
              <span className={styles.fileSize}>
                {(logoFile.size / 1024).toFixed(2)} KB
              </span>
              <button
                type="button"
                onClick={handleRemoveLogo}
                className={styles.removeButton}
                disabled={isSaving}
              >
                <FaIcons.FaTimes />
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {logoError && (
          <div className={styles.errorMessage}>
            <FaIcons.FaExclamationCircle />
            <span>{logoError}</span>
          </div>
        )}

        {/* Guidelines */}
        <div className={styles.guidelines}>
          <h4 className={styles.guidelinesTitle}>{translate('dashboard.companies.detail.logo.guidelines.title')}</h4>
          <ul className={styles.guidelinesList}>
            <li>{translate('dashboard.companies.detail.logo.guidelines.supportedFormats')}</li>
            <li>{translate('dashboard.companies.detail.logo.guidelines.maxFileSize')}</li>
            <li>{translate('dashboard.companies.detail.logo.guidelines.recommendedRatio')}</li>
            <li>{translate('dashboard.companies.detail.logo.guidelines.minResolution')}</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button
            variant="ghostOutline"
            onClick={handleClose}
            disabled={isSaving}
            fullWidth
          >
            {translate('global.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving || !logoFile}
            fullWidth
            leftIcon={<FaIcons.FaSave />}
          >
            {translate('dashboard.companies.detail.logo.actions.saveLogo')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateLogoModal;

